import { AlertCircleIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import type { ChatMessage, ChatNote, MessageClickHandler } from './chat-message.interface'
import { ChatMessageBubble } from './chat-message-bubble.atom'
import type { BubbleVariant } from './chat-message-item.helper'
import { StreamingMarkdownContent } from './streaming-markdown-content.molecule'

interface ChatMessageContentProps {
  message: ChatMessage | ChatNote
  bubbleVariant: BubbleVariant
  onClick?: MessageClickHandler
}

const ERROR_DEFAULT_MESSAGE = 'Failed to generate response'

/**
 * Message content with optional click handler, streaming indicator, and error state.
 */
const ChatMessageContentBase: React.FC<ChatMessageContentProps> = ({
  message,
  bubbleVariant,
  onClick,
}): ReactNode => {
  const isClickable = onClick && message.type === 'message'
  const isStreaming = 'isStreaming' in message && message.isStreaming
  const errorMessage = 'errorMessage' in message ? message.errorMessage : undefined

  const handleClick = (): void => {
    if (isClickable && message.type === 'message') {
      onClick(message)
    }
  }

  if (errorMessage) {
    return (
      <ChatMessageBubble variant="error" className="chat-message-error">
        <div className="flex items-center gap-2">
          <AlertCircleIcon className="size-4 flex-shrink-0" />
          <span className="text-sm">{ERROR_DEFAULT_MESSAGE}</span>
        </div>
      </ChatMessageBubble>
    )
  }

  return (
    <ChatMessageBubble variant={bubbleVariant} onClick={isClickable ? handleClick : undefined}>
      <StreamingMarkdownContent
        content={message.content}
        variant={bubbleVariant}
        isStreaming={isStreaming}
      />
      {isStreaming && (
        <span className="ml-1 inline-block size-2 animate-pulse rounded-full bg-current" />
      )}
    </ChatMessageBubble>
  )
}

export const ChatMessageContent = memo(ChatMessageContentBase)

ChatMessageContent.displayName = 'ChatMessageContent'
